import LegalLayout from "./LegalLayout";

const sections = [
  {
    heading: "Acknowledgment of Computational Intelligence",
    blocks: [
      { type: "p", text: "The user expressly acknowledges, understands, and agrees that certain platform outputs may be generated, enhanced, optimized, orchestrated, or supported by proprietary computational systems, including:" },
      { type: "ul", items: [
        "Artificial intelligence systems and machine learning systems",
        "Computational logic engines",
        "Predictive analytics frameworks",
        "Statistical inference models",
        "Decision-support engines",
        "Business rule systems",
        "Hybrid automation architectures",
        "Pattern-recognition systems",
        "Supply, market, and corridor intelligence engines",
        "Confidence scoring methodologies",
        "Operational learning frameworks",
      ]},
      { type: "p", text: "Such outputs include recommendations, predictive insights, workflow optimizations, behavioral alerts, confidence scores, operational indicators, matching systems, prioritization outputs, exception alerts, risk indicators, routing intelligence, opportunity rankings, and strategic recommendations — all owned exclusively by Delta GreenX." },
    ],
  },
  {
    heading: "Trade-Secret Components",
    blocks: [
      { type: "p", text: "The user further acknowledges that the following constitute highly confidential trade secrets and proprietary intellectual property of Delta GreenX:" },
      { type: "ul", items: [
        "Design, architecture, and weighting systems",
        "Scoring logic and model structures",
        "Training approaches and inference pathways",
        "Business rules and prompt orchestration systems",
        "Operational datasets and model tuning methodologies",
        "Exception logic and threshold frameworks",
        "Ranking methodologies",
        "Feedback systems and optimization pathways",
        "Output prioritization methodologies",
      ]},
    ],
  },
  {
    heading: "Prohibited Conduct",
    blocks: [
      { type: "p", text: "No user, investor, analyst, consultant, advisor, strategic partner, vendor, supplier, competitor, researcher, contractor, employee, or third-party representative may directly or indirectly:" },
      { type: "ul", items: [
        "Observe platform behavior to extract model logic or identify inference pathways",
        "Reconstruct prompt structures or map decision sequences",
        "Document system behavior for replication",
        "Compare outputs across scenarios to derive algorithmic patterns",
        "Recreate scoring systems or infer threshold values",
        "Benchmark performance for competitive intelligence",
        "Build training datasets from platform outputs",
        "Use outputs to fine-tune competing models",
        "Develop look-alike systems",
        "Conduct hidden product analysis",
        "Prepare technical documentation intended to replicate or commercialize similar computational systems",
      ]},
      { type: "p", text: "Users are further prohibited from:" },
      { type: "ul", items: [
        "Systematic interaction testing",
        "Repeated input-output analysis",
        "Simulation attacks or stress testing",
        "Edge-case probing or output sampling",
        "Session comparison analysis",
        "Workflow decomposition",
        "Decision pattern extraction",
        "Any other structured or unstructured behavior intended to reveal, infer, approximate, or replicate any component of Delta GreenX computational intelligence",
      ]},
    ],
  },
  {
    heading: "Investigative Rights",
    blocks: [
      { type: "p", text: "Delta GreenX reserves the unrestricted right to investigate suspected algorithmic misuse through:" },
      { type: "ul", items: [
        "Forensic session analysis",
        "Access pattern review",
        "Behavior modeling",
        "Output correlation analysis",
        "Metadata review and watermark analysis",
        "Infrastructure logs",
        "Device fingerprinting",
        "Network intelligence and identity tracing",
      ]},
    ],
  },
  {
    heading: "Consequences of Violation",
    blocks: [
      { type: "p", text: "Any attempt — successful or unsuccessful — to extract, infer, replicate, approximate, benchmark, clone, train against, commercially exploit, advise upon, publish about, license, or otherwise use Delta GreenX computational intelligence without prior written authorization shall constitute trade secret theft, intentional intellectual property misappropriation, unfair competition, unauthorized derivative development, and willful commercial exploitation." },
      { type: "p", text: "Any violation may result in:" },
      { type: "ul", items: [
        "Immediate access termination and permanent blacklisting",
        "Investor disqualification and contractual cancellation",
        "Injunctive relief and emergency restraining orders",
        "Direct, consequential, reputational, and business-interference damages",
        "Recovery of investigative costs, forensic expenses, and attorney fees",
        "Any other remedies available under applicable intellectual property, trade secret, competition, cybercrime, and commercial protection laws",
      ]},
      { type: "note", text: "The obligations contained in this section shall survive indefinitely regardless of account termination, investor rejection, contract completion, platform exit, or cessation of commercial discussions." },
    ],
  },
];

export default function AIProtection() {
  return (
    <LegalLayout
      title="AI, Algorithmic & Trade-Secret Protection"
      eyebrow="Legal · Computational Intelligence"
      effective="May 1, 2026"
      lastUpdated="May 1, 2026"
      intro={[
        "The Delta GreenX platform deploys proprietary computational intelligence — including AI systems, scoring models, and decision-support engines — owned exclusively by Delta GreenX.",
        "All architecture, weighting logic, scoring methodologies, and inference pathways are protected trade secrets.",
      ]}
      sections={sections}
      contact="legal@deltagreenx.com"
    />
  );
}
